// The app's few building blocks. Every control in the UI is one of these, so spacing, type and
// states stay the same everywhere. Their measurements are design system v1's (spec §11), read as
// --ba-* tokens in ui.css.

import * as RTooltip from '@radix-ui/react-tooltip';
import * as RSlider from '@radix-ui/react-slider';
import type { ComponentProps, ReactNode } from 'react';
import { Check, LucideProvider, type LucideIcon } from 'lucide-react';

/** Glyph sizes (`size.icon.*`): 14 in small controls and rows, 16 in buttons, 20 for the mark. */
export const ICON = { sm: 14, md: 16, lg: 20 } as const;

/** Every Lucide glyph draws a 1.5 px line at any size, as the screens' icons do. */
export function IconProvider({ children }: { children: ReactNode }) {
  return (
    <LucideProvider strokeWidth={1.5} nonScalingStroke>
      {children}
    </LucideProvider>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="kbd ba-type-kbd">{children}</kbd>;
}

export function Tip({ label, keys, side = 'bottom', children }: { label: string; keys?: string[]; side?: 'top' | 'bottom' | 'left' | 'right'; children: ReactNode }) {
  return (
    <RTooltip.Root>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content className="tip" side={side} sideOffset={8} collisionPadding={8}>
          <span>{label}</span>
          {keys?.length ? (
            <span className="tip-keys">
              {keys.map((k) => (
                <Kbd key={k}>{k}</Kbd>
              ))}
            </span>
          ) : null}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

/** `motion.tooltip-delay`: 500 ms (spec §11). */
const tooltipDelay = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ba-motion-tooltip-delay')) || 500;

export function TipProvider({ children }: { children: ReactNode }) {
  return (
    <RTooltip.Provider delayDuration={tooltipDelay()} skipDelayDuration={150}>
      {children}
    </RTooltip.Provider>
  );
}

/** sm: 24 px with a 14 px glyph (section heads, rows); md: 28 px with a 16 px glyph (the transport);
 *  lg: 32 px with a 20 px glyph (the bar, spec §3). */
type IconButtonProps = Omit<ComponentProps<'button'>, 'children'> & { icon: LucideIcon; label: string; keys?: string[]; active?: boolean; tipSide?: 'top' | 'bottom' | 'left' | 'right'; size?: 'sm' | 'md' | 'lg' };

export function IconButton({ icon: Icon, label, keys, active, tipSide, size = 'md', className, ...rest }: IconButtonProps) {
  return (
    <Tip label={label} keys={keys} side={tipSide}>
      <button type="button" aria-label={label} aria-pressed={active} className={`icon-btn ${size} ${active ? 'on' : ''} ${className ?? ''}`} {...rest}>
        <Icon size={ICON[size]} />
      </button>
    </Tip>
  );
}

type ButtonProps = ComponentProps<'button'> & { icon?: LucideIcon; variant?: 'default' | 'primary' | 'quiet' };

/** 28 px, type.small at medium weight, 10 px sides, a 14 px glyph 6 px before the label. */
export function Button({ icon: Icon, variant = 'default', className, children, ...rest }: ButtonProps) {
  return (
    <button type="button" className={`btn ba-type-small ${variant} ${className ?? ''}`} {...rest}>
      {Icon ? <Icon size={ICON.sm} /> : null}
      <span>{children}</span>
    </button>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  icon?: LucideIcon;
  /** Tooltip; the label is used when it's missing. */
  hint?: string;
}

/** md: 26 px segments on a 30 px track; sm: 22 on 26 (`component.segmented`). Text in type.small,
 *  the active segment at medium weight. */
export function Segmented<T extends string>({ value, options, onChange, label, size = 'md' }: { value: T | null; options: SegmentOption<T>[]; onChange: (v: T) => void; label: string; size?: 'sm' | 'md' }) {
  return (
    <div className={`seg ${size} ba-type-small`} role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button key={o.value} type="button" role="radio" aria-checked={value === o.value} aria-label={o.label} title={o.hint} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
            {Icon ? <Icon size={ICON.sm} /> : null}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The quiet icon-only segmented of a layer row (show · ghost · hide): no track, 24 × 22 cells,
 *  dim glyphs, one lit cell (spec §5). */
export function IconSegmented<T extends string>({ value, options, onChange, label }: { value: T | null; options: (SegmentOption<T> & { icon: LucideIcon })[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="vis" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <Tip key={o.value} label={o.hint ?? o.label}>
          <button type="button" role="radio" aria-checked={value === o.value} aria-label={o.label} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
            <o.icon size={ICON.sm} />
          </button>
        </Tip>
      ))}
    </div>
  );
}

export function Slider({ value, min, max, step, onChange, label }: { value: number; min: number; max: number; step: number; onChange: (v: number) => void; label: string }) {
  return (
    <RSlider.Root className="slider" value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0] ?? min)} aria-label={label}>
      <RSlider.Track className="slider-track">
        <RSlider.Range className="slider-range" />
      </RSlider.Track>
      <RSlider.Thumb className="slider-thumb" aria-label={label} />
    </RSlider.Root>
  );
}

export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} className={`switch ${checked ? 'on' : ''}`} onClick={() => onChange(!checked)}>
      <span className="switch-knob" />
    </button>
  );
}

/** A checkbox, for several of many in a list (spec §5; design's sheet: 14 px, Lucide's check at 12 px). */
export function Checkbox({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="checkbox" aria-checked={checked} aria-label={label} className="checkbox" onClick={() => onChange(!checked)}>
      <Check size={12} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

/** A panel section: an overline in a 22 px head row, with at most one action at its right. */
export function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <header className="section-head">
        <h3 className="ba-type-overline">{title}</h3>
        {action}
      </header>
      {children}
    </section>
  );
}
